import React, { ChangeEvent, useState } from 'react';
import { MediumHeader } from '../../ui/Header/Header';
import Button from '../../ui/Button/BlockButton';
import TextArea from '../../ui/TextArea/TextArea';
import TextLink, { TextLinkTargetType } from '../../ui/TextLink/TextLink';
import StarRating from './StarRating/StarRating';
import { useTheme } from '../../../hooks/useTheme';
import { useSettings } from '../../../hooks/useSettings';
import { useTranslations } from '../../../hooks/useTranslations';
import styles from './Portrait.module.css';

export interface PortraitProps {
  featureName?: string;
  selectedRating: number;
  onRatingSelect: (index: number) => void;
  onClickSupportLink?: () => void;
  submitFeedback?: (feedbackComment: string) => void;
  commentOptional?: boolean;
}

const Portrait = ({
  featureName,
  selectedRating,
  onRatingSelect,
  onClickSupportLink,
  submitFeedback,
  commentOptional,
}: PortraitProps) => {
  const {
    feedbackButtonText = '',
    feedbackCommentLabel = '',
    feedbackCustomerSupportLink = '',
    feedbackExperienceHeader = '',
  } = useTranslations();

  const {
    linksNoneDefault: textLinkStyle,
    linksNoneHover: textLinkHoverStyle,
  } = useTheme();

  const { feedback } = useSettings();

  const { enableInitialCollapse: initialCollapsed, customerSupportLink } =
    feedback || {};

  const [feedbackComment, setFeedbackComment] = useState('');
  const [collapsed, setCollapsed] = useState(Boolean(initialCollapsed));

  return (
    <div data-test="feedbackPortraitView">
      <div className={styles.portraitHeader}>
        {feedbackExperienceHeader && (
          <MediumHeader as="p">{feedbackExperienceHeader}</MediumHeader>
        )}
      </div>
      <StarRating
        onClick={(index: number) => {
          onRatingSelect(index);
          setCollapsed(false);
        }}
        selectedRating={selectedRating}
        featureName={featureName}
      />
      {!collapsed && (
        <div data-test="feedbackPortraitExpandedBlock">
          <div className={styles.feedbackBox}>
            <TextArea
              id="feedbackCommentBox"
              animateLabel
              label={feedbackCommentLabel || ''}
              featureName={featureName}
              onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                setFeedbackComment(event.target.value)
              }
            />
          </div>
          <div className={styles.feedbackAction}>
            <Button
              disabled={!commentOptional && !feedbackComment.length}
              big
              onClick={() => {
                if (submitFeedback) submitFeedback(feedbackComment);
              }}
            >
              {feedbackButtonText}
            </Button>
          </div>
          {customerSupportLink && (
            <TextLink
              target={TextLinkTargetType.BLANK}
              href={customerSupportLink}
              linkStyle={{
                sDefault: textLinkStyle,
                sHover: textLinkHoverStyle,
              }}
              onClick={() => {
                if (onClickSupportLink) onClickSupportLink();
              }}
            >
              {feedbackCustomerSupportLink}
            </TextLink>
          )}
        </div>
      )}
    </div>
  );
};

export default Portrait;
